import React, { FC, useEffect, useState } from 'react';
import { injectIntl, IntlShape } from 'react-intl';
import { get, isEmpty, isNil, uniqueId } from 'lodash';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import DialogCivilProceeding from 'components/DialogCivilProceeding';
import {
  CLAIM_TYPE_INIT_STATE,
  DAMAGE_INIT_STATE,
  OTHER_DAMAGE_INIT_STATE,
  OTHER_PROPERTIES_DESCRIPTION_SIZE_LIMIT,
  VEHICLE_FORM_INIT_STATE,
  VEHICLE_FORM_VALUES_INIT_STATE,
  VICTIM_FORM_INIT_STATE,
  VICTIM_FORM_VALUES_INIT_STATE,
} from 'containers/CourtManagerLegalProceedings/constants';
import {
  DamageType,
  VehicleForm,
} from 'containers/CourtManagerLegalProceedings/typings';
import {
  extractUpdatedDocument,
  filterVehicle,
  filterVictim,
  formatOpposingParties,
  getRiskIdFromPolicyInfo,
} from 'containers/CourtManagerLegalProceedings/utils';
import * as featureService from 'features/fflip-helpers';
import { FEATURES } from 'features/features';
import CivilProceedingForm from 'containers/LawyerLegalProceedingsPage/CivilProceeding/CivilProceedingForm';
import { translate } from 'containers/services/translate';
import messages from 'containers/LawyerLegalProceedingsPage/CivilProceeding/messages';
import { Listings } from 'containers/AppContainer/typings';
import { IUploadedDocument } from 'containers/LawyerLegalProceedingsPage/typings';
import currentIntervenantStorage from 'containers/services/security/current-intervenant-storage';
import { ALL_STATUS } from 'containers/LawyerLegalProceedingsPage/CivilProceedingRecap/constants';
import { formsAreValidated } from 'containers/services/validateForm';
import { victimValidator } from 'containers/RecapManagerPage/VictimDrawer';
import {
  formatCourtFileNumber,
  validationCourtFileNumber,
} from 'containers/LawyerLegalProceedingsPage/judgementAdd/utils';
import {
  CIVIL_PROCEEDING_INFORMATIONS_FIELDS,
  CIVIL_PROCEEDING_LAWYER_INFORMATIONS_FIELDS,
  LISTINGS,
} from 'containers/data-structure';
import { PROFESSION_CODES } from 'containers/LawyerLegalProceedingsPage/JudgementOnMerits/constants';
import { addPriorityReasonProfession, validationVehicle } from 'services/utils';
import { isPendingApprovalClaim } from 'containers/LawyerLegalProceedingsPage/utils';
import { VictimType } from 'typings/globalTypes';
import { ClaimRecapType } from 'typings/claimTypes';
import { getAssignedJurisdictionLawyerCode } from 'containers/RecapManagerPage/utils';

export interface CivilProceedingProps {
  requestCreateCivilProceeding: Function;
  intl: IntlShape;
  claimRecap: ClaimRecapType;
  setIsDialogOpenCivilProceeding: Function;
  claimId: number;
  isDialogOpenCivilProceeding: boolean;
  procedureType: any;
  listings: Listings;
  resetUploadDocumentStore: Function;
  uploadDocument: Function;
  uploadedDocument: IUploadedDocument;
  documentId: string;
  setDocumentId: Function;
  requestCorporealLawyers: Function;
  corporealLawyers: any;
  policyInfo: { sagis: {} };
  sagisVictimInfo: any;
  sagisOpposingParties: any;
  updateCivilProcedure: boolean;
  procedureInfo: any;
  requestUpdateCivilProceeding: Function;
}

export const CivilProceeding: FC<CivilProceedingProps> = props => {
  const priorityReasonClaimId = useSelector((state: any) =>
    get(state.toJS(), ['recap', 'claimRecap', 'claim', 'priorityReasonId']),
  );
  const [otherDamage, setOtherDamage] = useState(OTHER_DAMAGE_INIT_STATE);
  const [damage, setDamage] = useState<DamageType>(DAMAGE_INIT_STATE);
  const [claimType, setClaimType] = useState({
    corporeal: false,
    material: false,
  });
  const [victimValidation, setVictimValidation] = useState({});
  const [civilProceedingInfo, setCivilProceedingInfo] = React.useState<
    null | any
  >({});
  const [victimFormValues, setVictimFormValues] = useState<{
    [key: string]: VictimType;
  }>({});
  const claimNumber = get(props.claimRecap, 'claim.claimNumber');
  const [civilProceedingDoc, setCivilProceedingDoc] = React.useState<
    null | any
  >([]);

  const addVictimForm = () => {
    setVictimFormValues(prevVictimFormValues => {
      return {
        ...prevVictimFormValues,
        [uniqueId()]: VICTIM_FORM_INIT_STATE,
      } as any;
    });
  };
  const [associatedVictims, setAssociatedVictims] = useState<any[]>([]);
  const [vehicleFormValues, setVehicleFormValues] = useState<{
    [key: string]: VehicleForm;
  }>({});
  const [associatedOpposingParties, setAssociatedOpposingParties] = useState<
    any[]
  >([]);
  const [victimsInfo, setVictimsInfo] = React.useState<null | any>([]);

  useEffect(() => {
    if (props.isDialogOpenCivilProceeding === false) {
      cleanCivilProceedingStates();
    }
    if (props.updateCivilProcedure && props.procedureInfo) {
      handleOpenUpdateFormCivilProceeding(props.procedureInfo);
    }
  }, [
    props.isDialogOpenCivilProceeding,
    props.updateCivilProcedure,
    props.procedureInfo,
  ]);

  const cleanCivilProceedingStates = () => {
    props.setIsDialogOpenCivilProceeding(false);
    setCivilProceedingInfo({});
    setOtherDamage(OTHER_DAMAGE_INIT_STATE);
    setDamage(DAMAGE_INIT_STATE);
    setClaimType(CLAIM_TYPE_INIT_STATE);
    setVehicleFormValues(VEHICLE_FORM_VALUES_INIT_STATE);
    setVictimFormValues(VICTIM_FORM_VALUES_INIT_STATE);
    setCivilProceedingDoc([]);
  };

  const successCallBack = () => {
    toast.success(translate(messages.successDialogCivilProceeding, props));
    cleanCivilProceedingStates();
  };

  const riskId = getRiskIdFromPolicyInfo(props?.policyInfo);

  const handleValidateProceedingCivil = () => {
    let isFormValid = true;
    [...Object.values(victimFormValues)].forEach((el: any) => {
      const validator = victimValidator(el);
      setVictimValidation(validator);
      if (!formsAreValidated(validator ? validator : {})) {
        isFormValid = false;
      }
    });
    if (
      new Date(civilProceedingInfo.hearingDate) <
      new Date(get(props.policyInfo, 'corporeal[0].lossDate', ''))
    ) {
      isFormValid = false;
    }
    if (isFormValid) {
      props.requestCreateCivilProceeding(
        {
          claimId: props.claimId,
          circonscription: civilProceedingInfo.circonscription,
          assignedLawyer: currentIntervenantStorage.get().sagis_code,
          procedureNatureId: props.procedureType,
          town: civilProceedingInfo.town,
          claimNumbers: claimNumber,
          isInboxesActive: featureService.hasFeature(
            FEATURES.ATTRIBUTION_INBOXES_MANAGER,
          ),
          riskKey: riskId,
          opposingLawyer: civilProceedingInfo.opposingLawyer,
          jurisdictionDegreeId: civilProceedingInfo.jurisdictionDegreeId,
          receptionDate: civilProceedingInfo.receptionDate,
          hearingDate: civilProceedingInfo.hearingDate,
          documents: civilProceedingDoc,
          courtNumber: formatCourtFileNumber(civilProceedingInfo.courtNumber),
          isTrackingActive: featureService.hasFeature(
            FEATURES.TRACKING_ACTIONS_USER,
          ),
          victims: claimType.corporeal
            ? [
                ...Object.values(victimFormValues).map((el: any) => {
                  return {
                    ...el,
                    itt: el.itt === '' ? null : el.itt,
                    isExcluded: !!el.isExcluded,
                    annualOfIncome:
                      el.annualOfIncome === '' ? null : el.annualOfIncome,
                    id: undefined,
                    beneficiaries: el.beneficiaries.map((item: any) => ({
                      ...item,
                      funeralFees:
                        item.funeralFees === '' ? undefined : item.funeralFees,
                    })),
                  };
                }),
              ]
            : undefined,
          priorityReasonId: addPriorityReasonProfession(
            priorityReasonClaimId,
            !![...Object.values(victimFormValues)].find((v: any) =>
              PROFESSION_CODES.includes(v.professionCode),
            ),
            props.listings,
          ),
          associatedVictims,
          username: currentIntervenantStorage.get().preferred_username,
          creator: currentIntervenantStorage.get().preferred_username,
          otherDamage: damage.otherProperties ? otherDamage : undefined,
          opposingParties: damage.adverseVehicles
            ? formatOpposingParties(
                Object.values(vehicleFormValues),
                riskId,
                claimNumber,
                props.claimId,
                props.listings?.compagnies,
              )
            : undefined,
          associatedOpposingParties,
          status: ALL_STATUS.PENDING,
          isLawyerInboxesActive: featureService.hasFeature(
            FEATURES.LAWYER_INBOXES,
          ),
        },
        successCallBack,
      );
    }
  };
  const successCallBackUpdate = () => {
    toast.success(translate(messages.updatedRequest, props));
    cleanCivilProceedingStates();
  };
  const handleUpdateProceedingCivil = () => {
    const date = new Date(get(props.claimRecap, 'claim.lossDate'));
    const dayOfMonth = date.getDate();
    const lawyerJurisdictionAssignedCode = featureService.hasFeature(
      FEATURES.ASSIGNEMENT_JURISDICTION_LAWYER_REQUEST,
    )
      ? getAssignedJurisdictionLawyerCode(
          get(props.listings, LISTINGS.MAPPING_LAWYER_JURISDICTION, []),
          civilProceedingInfo.town,
          dayOfMonth,
        )
      : '';
    props.requestUpdateCivilProceeding(
      {
        id: civilProceedingInfo.id,
        isTrackingActive: featureService.hasFeature(
          FEATURES.TRACKING_ACTIONS_USER,
        ),
        isLawyer: true,
        JudiciaryProcessInfo: {
          claimId: props.claimId,
          circonscription: civilProceedingInfo.circonscription,
          assignedLawyer: featureService.hasFeature(
            FEATURES.ASSIGNEMENT_JURISDICTION_LAWYER_REQUEST,
          )
            ? lawyerJurisdictionAssignedCode
            : civilProceedingInfo.assignedLawyer,

          town: civilProceedingInfo.town,
          claimNumbers: claimNumber,
          updateSagisIntervenantProvision: featureService.hasFeature(
            FEATURES.EVOLUTION_P3,
          )
            ? false
            : true,
          opposingLawyer: civilProceedingInfo.opposingLawyer,
          jurisdictionDegreeId: civilProceedingInfo.jurisdictionDegreeId,
          receptionDate: civilProceedingInfo.receptionDate,
          hearingDate: civilProceedingInfo.hearingDate,
          isTrackingActive: featureService.hasFeature(
            FEATURES.TRACKING_ACTIONS_USER,
          ),
          courtNumber: formatCourtFileNumber(civilProceedingInfo.courtNumber),
          victims: claimType.corporeal
            ? [...Object.values(victimFormValues)].map((el: any) => ({
                ...el,
                itt: el.itt === '' ? null : el.itt,
                isExcluded: !!el.isExcluded,
              }))
            : undefined,
          associatedVictims: claimType.corporeal ? associatedVictims : [],
          username: currentIntervenantStorage.get()?.preferred_username,
          isLawyer: true,
          otherDamage:
            damage.otherProperties && claimType.material
              ? otherDamage
              : undefined,
          opposingParties: damage.adverseVehicles
            ? formatOpposingParties(
                Object.values(vehicleFormValues),
                riskId,
                claimNumber,
                props.claimId,
                props.listings?.compagnies,
              )
            : undefined,
          associatedOpposingParties:
            claimType.material && damage.adverseVehicles
              ? associatedOpposingParties
              : [],
          isCourtMonitoringManager: false,
          status: ALL_STATUS.PENDING,
        },
        documents: extractUpdatedDocument(civilProceedingDoc),
      },
      successCallBackUpdate,
    );
  };

  const handleCloseCivilProceeding = () => {
    setOtherDamage(OTHER_DAMAGE_INIT_STATE);
    setDamage(DAMAGE_INIT_STATE);
    props.setIsDialogOpenCivilProceeding(false);
  };

  const addVehicleForm = () => {
    setVehicleFormValues(prevVehicleFormValues => {
      return {
        ...prevVehicleFormValues,
        [uniqueId()]: VEHICLE_FORM_INIT_STATE,
      };
    });
  };
  const handleOpenUpdateFormCivilProceeding = (payload: any) => {
    setCivilProceedingInfo({
      ...payload,
      dateReceiptRequest: payload.receptionDate,
    } as any);
    setAssociatedVictims(payload.victims.map((item: any) => item.id));
    setClaimType({
      corporeal: !isEmpty(payload.victims),
      material: !isEmpty(payload.vehicules) || !isEmpty(payload.otherDamage),
    });
    setAssociatedOpposingParties(payload.vehicules.map((item: any) => item.id));
    setDamage({
      adverseVehicles: !isEmpty(payload.vehicules),
      otherProperties: !isEmpty(payload.otherDamage),
    });
    setCivilProceedingDoc(get(payload, 'documents'));
    setOtherDamage(get(payload, 'otherDamage.[0]', OTHER_DAMAGE_INIT_STATE));
  };

  const handleChangeOtherDamage = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOtherDamage({
      description: e.target.value.substring(
        0,
        OTHER_PROPERTIES_DESCRIPTION_SIZE_LIMIT,
      ),
    });
  };
  return (
    <DialogCivilProceeding
      open={props.isDialogOpenCivilProceeding}
      title={
        props.procedureType === 2
          ? translate(messages.civilProceedingTitle, props)
          : translate(messages.commercialproceedingTitle, props)
      }
      handleClose={handleCloseCivilProceeding}
      confirmTitle={
        props.updateCivilProcedure
          ? translate(messages.updateTitle, props)
          : translate(messages.civilAdd, props)
      }
      cancelTitle={translate(messages.civilCancel, props)}
      handleValidate={
        props.updateCivilProcedure
          ? handleUpdateProceedingCivil
          : handleValidateProceedingCivil
      }
      disablePrimaryButton={
        !validationCourtFileNumber(
          get(civilProceedingInfo, [
            CIVIL_PROCEEDING_INFORMATIONS_FIELDS.COURT_NUMBER,
          ]),
        ) ||
        (claimType.corporeal && !claimType.material
          ? isEmpty(associatedVictims) && isEmpty(victimFormValues)
          : !claimType.corporeal && claimType.material
          ? (isEmpty(associatedOpposingParties) &&
              isEmpty(vehicleFormValues)) ||
            (!isEmpty(vehicleFormValues) &&
              !validationVehicle(Object.values(vehicleFormValues)))
          : (isEmpty(associatedVictims) && isEmpty(victimFormValues)) ||
            (isEmpty(associatedOpposingParties) &&
              isEmpty(vehicleFormValues)) ||
            (!isEmpty(vehicleFormValues) &&
              !validationVehicle(Object.values(vehicleFormValues)))) ||
        (featureService.hasFeature(
          FEATURES.ASSIGNEMENT_JURISDICTION_LAWYER_REQUEST,
        ) &&
          isNil(
            get(
              civilProceedingInfo,
              [CIVIL_PROCEEDING_LAWYER_INFORMATIONS_FIELDS.TOWN],
              null,
            ),
          ))
      }
      children={
        <CivilProceedingForm
          resetUploadedDocument={props.resetUploadDocumentStore}
          uploadDocument={props.uploadDocument}
          uploadedDocument={props.uploadedDocument}
          civilProceedingDoc={civilProceedingDoc}
          setCivilProceedingDoc={setCivilProceedingDoc}
          victimsInfo={victimsInfo}
          setVictimsInfo={setVictimsInfo}
          setDocumentId={props.setDocumentId}
          documentId={props.documentId}
          civilProceedingInfo={civilProceedingInfo}
          setCivilProceedingInfo={setCivilProceedingInfo}
          victims={
            isPendingApprovalClaim(props.claimRecap)
              ? get(props.claimRecap, 'victims', [])
              : filterVictim(get(props.claimRecap, 'victims', []))
          }
          vehicles={
            isPendingApprovalClaim(props.claimRecap)
              ? get(props.claimRecap, 'vehicules', [])
              : filterVehicle(get(props.claimRecap, 'vehicules', []))
          }
          listings={props.listings}
          requestCorporealLawyers={props.requestCorporealLawyers}
          corporealLawyers={props.corporealLawyers}
          claimId={props.claimId}
          damage={damage}
          setDamage={setDamage}
          claimType={claimType}
          setClaimType={setClaimType}
          otherDamage={otherDamage}
          handleChangeOtherDamage={handleChangeOtherDamage}
          vehicleFormValues={vehicleFormValues}
          setVehicleFormValues={setVehicleFormValues}
          addVehicleForm={addVehicleForm}
          victimFormValues={victimFormValues}
          setVictimFormValues={setVictimFormValues}
          addVictimForm={addVictimForm}
          associatedVictims={associatedVictims}
          setAssociatedVictims={setAssociatedVictims}
          associatedOpposingParties={associatedOpposingParties}
          setAssociatedOpposingParties={setAssociatedOpposingParties}
          lossDate={get(props.claimRecap, 'claim.lossDate')}
          victimValidation={victimValidation}
          sagisVictimInfo={props.sagisVictimInfo}
          sagisOpposingParties={props.sagisOpposingParties}
          claimRecap={props.claimRecap}
        />
      }
    />
  );
};
export default injectIntl(CivilProceeding);
